import type {
  BranchProgressCount,
  EmployeeProgressRow,
  ProgressStage,
} from './ports/progressPort';

/**
 * Turns progress rows into what the dashboard shows.
 *
 * Pure, so the arithmetic the committee will act on can be tested without a
 * database (§5.4).
 */

export const STAGE_LABELS_AR: Readonly<Record<ProgressStage, string>> = {
  not_started: 'لم يبدأ',
  in_progress: 'قيد التعبئة',
  submitted: 'مُقدَّم',
};

export const STAGE_TONES: Readonly<
  Record<ProgressStage, 'neutral' | 'warning' | 'success'>
> = {
  not_started: 'neutral',
  in_progress: 'warning',
  submitted: 'success',
};

export interface OverallProgress {
  readonly total: number;
  readonly notStarted: number;
  readonly inProgress: number;
  readonly submitted: number;
  /** Whole percent submitted, for the headline figure. */
  readonly submittedPercent: number;
}

export function summarise(counts: readonly BranchProgressCount[]): OverallProgress {
  const total = counts.reduce((sum, entry) => sum + entry.total, 0);
  const notStarted = counts.reduce((sum, entry) => sum + entry.notStarted, 0);
  const inProgress = counts.reduce((sum, entry) => sum + entry.inProgress, 0);
  const submitted = counts.reduce((sum, entry) => sum + entry.submitted, 0);

  return {
    total,
    notStarted,
    inProgress,
    submitted,
    // Guarded: an empty fund would otherwise report NaN%, which reads as a bug
    // to whoever is looking at it.
    submittedPercent: total === 0 ? 0 : Math.round((submitted / total) * 100),
  };
}

/**
 * Rows that need a person to do something, most urgent first.
 *
 * A dashboard listing 800 names sorted by employee number tells the committee
 * nothing. What they need is who to chase.
 */
export function needsAttention(
  rows: readonly EmployeeProgressRow[],
): readonly EmployeeProgressRow[] {
  const weight = (row: EmployeeProgressRow): number => {
    // A disputed email mapping is a data error blocking a real person, and it
    // will keep mismatching others until HR fixes the row.
    if (row.emailMappingDisputed) return 0;
    // Never signed in at all.
    if (!row.linked) return 1;
    // Signed in but has not started.
    if (row.stage === 'not_started') return 2;
    // Started but fewer dependants than the roster expects.
    if (row.dependentsEntered < row.declaredChildrenCount) return 3;
    if (row.stage === 'in_progress') return 4;
    return 5;
  };

  return rows
    .filter((row) => weight(row) < 5)
    .slice()
    .sort((a, b) => weight(a) - weight(b));
}

/** Why a row is being chased, so the list explains itself. */
export function attentionReasonAr(row: EmployeeProgressRow): string {
  if (row.emailMappingDisputed) return 'البريد مربوط باسم خاطئ — يلزم تصحيح السجل';
  if (!row.linked) return 'لم يسجّل الدخول بعد';
  if (row.stage === 'not_started') return 'لم يبدأ التعبئة';
  if (row.dependentsEntered < row.declaredChildrenCount) {
    const missing = row.declaredChildrenCount - row.dependentsEntered;
    return `ناقص ${String(missing)} من المعالين`;
  }
  return 'قيد التعبئة';
}
